import React, { useState, useEffect } from 'react';
import api from '../services/api';

const emptyProject = { name: '', description: '', platform: 'CF', stack: 'SPRING_BOOT', repoUrl: '', repoSubfolder: '', branch: 'main', localPath: '', cfApiUrl: '', cfOrg: '', cfSpace: '', cfAppName: '', healthCheckUrl: '', healthCheckIntervalSec: 300, status: 'ACTIVE', companyId: '' };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyProject });
  const [editing, setEditing] = useState(null);
  const [healthResults, setHealthResults] = useState({});
  const [companies, setCompanies] = useState([]);
  const [viewProject, setViewProject] = useState(null);
  const [cfApps, setCfApps] = useState([]);
  const [cfAppsLoading, setCfAppsLoading] = useState(false);
  const [appActionLoading, setAppActionLoading] = useState('');
  const [actionOutput, setActionOutput] = useState('');
  const [envVars, setEnvVars] = useState([]);
  const [envForm, setEnvForm] = useState({ key: '', value: '', masked: true });
  const [envSaving, setEnvSaving] = useState(false);
  const [showEnvValues, setShowEnvValues] = useState({});

  const load = () => {
    api.get('/projects').then(r => setProjects(r.data));
    api.get('/companies').then(r => setCompanies(r.data));
  };
  useEffect(() => { load(); }, []);

  const checkHealth = async (project) => {
    if (!project.healthCheckUrl) return alert('No Health Check URL configured for this project.');
    setHealthResults(prev => ({ ...prev, [project.id]: { status: 'checking' } }));
    try {
      const start = Date.now();
      const res = await fetch(project.healthCheckUrl, { mode: 'no-cors', signal: AbortSignal.timeout(10000) });
      const ms = Date.now() - start;
      setHealthResults(prev => ({ ...prev, [project.id]: { status: 'up', ms, code: res.status } }));
    } catch (e) {
      setHealthResults(prev => ({ ...prev, [project.id]: { status: 'down', error: e.message } }));
    }
  };

  const loadEnvVars = async (projectId) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/env-vars`);
      setEnvVars(data);
    } catch { setEnvVars([]); }
  };

  const saveEnvVar = async (projectId) => {
    if (!envForm.key.trim() || !envForm.value.trim()) return alert('Key and value are required');
    setEnvSaving(true);
    try {
      await api.post(`/projects/${projectId}/env-vars`, { key: envForm.key.trim(), value: envForm.value.trim(), masked: envForm.masked });
      setEnvForm({ key: '', value: '', masked: true });
      loadEnvVars(projectId);
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setEnvSaving(false);
  };

  const deleteEnvVar = async (projectId, id) => {
    if (!window.confirm('Delete this variable?')) return;
    await api.delete(`/projects/${projectId}/env-vars/${id}`);
    loadEnvVars(projectId);
  };

  const openView = async (project) => {
    setViewProject(project);
    setShowForm(false);
    setCfApps([]);
    setActionOutput('');
    setEnvVars([]);
    setEnvForm({ key: '', value: '', masked: true });
    loadEnvVars(project.id);
    if (project.platform === 'CF') {
      setCfAppsLoading(true);
      try {
        const { data } = await api.get(`/projects/${project.id}/cf/apps`);
        const allApps = data.apps || [];
        const appName = project.cfAppName;
        if (appName) {
          setCfApps(allApps.filter(a => a.name === appName || a.name === appName + '-idle' || a.name === appName + '-live' || a.name.startsWith(appName + '-')));
        } else {
          setCfApps(allApps);
        }
      } catch (e) {
        setActionOutput('Error loading apps: ' + (e.response?.data?.error || e.message));
      }
      setCfAppsLoading(false);
    }
  };

  const refreshApps = () => viewProject && openView(viewProject);

  const appAction = async (appName, action) => {
    if (action === 'stop' && !window.confirm(`Stop "${appName}"?`)) return;
    setAppActionLoading(appName + action);
    setActionOutput(`⏳ ${action}ing ${appName}...`);
    try {
      const { data } = await api.post(`/projects/${viewProject.id}/cf/app/${appName}/${action}`, {});
      setActionOutput(data.output);
      setTimeout(refreshApps, 2000);
    } catch (e) {
      setActionOutput('Error: ' + (e.response?.data?.error || e.message));
    }
    setAppActionLoading('');
  };

  const save = async () => {
    if (!form.name) return alert('Project name is required');
    if (editing) {
      await api.put(`/projects/${editing}`, form);
    } else {
      await api.post('/projects', form);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ ...emptyProject });
    load();
  };

  const edit = (p) => { setForm({ ...p }); setEditing(p.id); setShowForm(true); setViewProject(null); };
  const del = async (id) => { if (window.confirm('Delete this project?')) { await api.delete(`/projects/${id}`); load(); } };

  const renderField = (label, field, type, options) => (
    <div key={field} style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>{label}</label>
      {options ? (
        <select value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type || 'text'} value={form[field] || ''} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
      )}
    </div>
  );

  const stateBadge = (state) => {
    const s = (state || '').toLowerCase();
    if (s === 'started') return <span className="badge badge-success">STARTED</span>;
    if (s === 'stopped') return <span className="badge badge-danger">STOPPED</span>;
    return <span className="badge badge-gray">{state}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>Projects</h1>
          <p style={{ color: '#64748b', fontSize: 14 }}>Manage your CLTS applications</p>
        </div>
        <button className="btn-primary" onClick={() => { setForm({ ...emptyProject }); setEditing(null); setShowForm(true); setViewProject(null); }}>+ Add Project</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16, fontWeight: 600 }}>{editing ? 'Edit Project' : 'New Project'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
            {renderField("Project Name", "name")}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Company</label>
              <select value={form.companyId || ''} onChange={e => setForm(f => ({ ...f, companyId: e.target.value ? Number(e.target.value) : null }))}>
                <option value="">-- No Company --</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {renderField("Description", "description")}
            {renderField("Platform", "platform", null, ['CF', 'K8S', 'LOCAL'])}
            {renderField("Stack", "stack", null, ['SPRING_BOOT', 'SAP_CAP', 'REACT', 'ANGULAR', 'NODE'])}
            {renderField("Repository URL", "repoUrl")}
            {renderField("Repo Subfolder (if project is inside a subfolder)", "repoSubfolder")}
            {renderField("Branch", "branch")}
            {renderField("Local Path", "localPath")}
            {renderField("Health Check URL", "healthCheckUrl")}
            {renderField("CF API URL", "cfApiUrl")}
            {renderField("CF Org", "cfOrg")}
            {renderField("CF Space", "cfSpace")}
            {renderField("CF App Name", "cfAppName")}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn-primary" onClick={save}>Save</button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: viewProject ? '1fr 1fr' : '1fr', gap: 20 }}>
        <div className="card">
          {projects.length === 0 ? <p style={{ color: '#9ca3af' }}>No projects yet. Add your first project.</p> : (
            <table>
              <thead><tr><th>Name</th><th>Company</th><th>Platform</th><th>Stack</th><th>Health</th><th>Actions</th></tr></thead>
              <tbody>
                {projects.map(p => {
                  const h = healthResults[p.id];
                  return (
                  <tr key={p.id} style={{ background: viewProject?.id === p.id ? 'var(--primary-light)' : '' }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.name} <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(#{p.id})</span></div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{p.description}</div>
                      {p.cfAppName && <div style={{ fontSize: 11, color: '#7c3aed' }}>CF: {p.cfAppName}</div>}
                    </td>
                    <td style={{ fontSize: 13 }}>{companies.find(c => c.id === p.companyId)?.name || <span style={{ color: '#9ca3af' }}>-</span>}</td>
                    <td><span className="badge badge-info">{p.platform}</span></td>
                    <td style={{ fontSize: 13 }}>{p.stack}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button className="btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => checkHealth(p)}
                          disabled={h && h.status === 'checking'}>
                          {h && h.status === 'checking' ? '⏳' : '🔍'} Check
                        </button>
                        {h && h.status === 'up' && <span className="badge badge-success" title={`${h.ms}ms`}>UP ({h.ms}ms)</span>}
                        {h && h.status === 'down' && <span className="badge badge-danger" title={h.error}>DOWN</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {p.platform === 'CF' && (
                          <button className="btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openView(p)}>View</button>
                        )}
                        <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => edit(p)}>Edit</button>
                        <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => del(p.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* CF Apps Detail Panel */}
        {viewProject && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h3 style={{ fontWeight: 600 }}>{viewProject.name}</h3>
              <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setViewProject(null)}>Close</button>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              <span>{viewProject.cfOrg} / {viewProject.cfSpace}</span>
              {viewProject.cfApiUrl && <span> • {viewProject.cfApiUrl}</span>}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600 }}>CF Applications ({cfApps.length})</h4>
              <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={refreshApps} disabled={cfAppsLoading}>
                {cfAppsLoading ? '⏳ Loading...' : '↻ Refresh'}
              </button>
            </div>

            {cfAppsLoading && cfApps.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>⏳ Fetching apps (logging in to CF)...</p>
            ) : cfApps.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>No apps found in this space.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cfApps.map((app, i) => (
                  <div key={i} style={{
                    padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{app.name}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                          {stateBadge(app.state)}
                          <span>Instances: {app.instances}</span>
                          {app.memory && <span>Memory: {app.memory}</span>}
                          {app.disk && <span>Disk: {app.disk}</span>}
                        </div>
                        {app.urls && (
                          <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 4 }}>
                            <a href={`https://${app.urls}`} target="_blank" rel="noreferrer">{app.urls}</a>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {(app.state || '').toLowerCase() === 'stopped' ? (
                          <button className="btn-success" style={{ padding: '4px 12px', fontSize: 11 }}
                            disabled={appActionLoading === app.name + 'start'}
                            onClick={() => appAction(app.name, 'start')}>
                            {appActionLoading === app.name + 'start' ? '⏳' : '▶'} Start
                          </button>
                        ) : (
                          <button className="btn-danger" style={{ padding: '4px 12px', fontSize: 11 }}
                            disabled={appActionLoading === app.name + 'stop'}
                            onClick={() => appAction(app.name, 'stop')}>
                            {appActionLoading === app.name + 'stop' ? '⏳' : '⏹'} Stop
                          </button>
                        )}
                        <button className="btn-ghost" style={{ padding: '4px 12px', fontSize: 11 }}
                          disabled={appActionLoading === app.name + 'restart'}
                          onClick={() => appAction(app.name, 'restart')}>
                          {appActionLoading === app.name + 'restart' ? '⏳' : '🔄'} Restart
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Environment Variables */}
            <div style={{ marginTop: 24, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>🔐 Environment Variables</h4>

              {/* Add new env var */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <input placeholder="KEY" value={envForm.key} onChange={e => setEnvForm(f => ({ ...f, key: e.target.value }))}
                  style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', width: 140, fontFamily: 'monospace' }} />
                <input placeholder="value" value={envForm.value} onChange={e => setEnvForm(f => ({ ...f, value: e.target.value }))}
                  type={envForm.masked ? 'password' : 'text'}
                  style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', width: 200 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
                  <input type="checkbox" checked={envForm.masked} onChange={e => setEnvForm(f => ({ ...f, masked: e.target.checked }))} />
                  Mask
                </label>
                <button onClick={() => saveEnvVar(viewProject.id)} disabled={envSaving}
                  style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: 'none', background: '#1a56db', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  {envSaving ? 'Saving...' : '+ Add / Update'}
                </button>
              </div>

              {/* List */}
              {envVars.length === 0 ? (
                <p style={{ fontSize: 12, color: '#9ca3af' }}>No env vars yet. Add variables above — they'll be injected into every build.</p>
              ) : (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' }}>Key</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' }}>Value</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' }}>Type</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e2e8f0' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {envVars.map(v => (
                      <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{v.varKey}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: '#6b7280' }}>
                          {v.masked
                            ? (showEnvValues[v.id] ? '(re-enter to view)' : '••••••••')
                            : v.varValue}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          {v.masked
                            ? <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 10, background: '#fef3c7', color: '#92400e' }}>🔒 Secret</span>
                            : <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 10, background: '#f1f5f9', color: '#6b7280' }}>Plain</span>}
                        </td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                          <button onClick={() => deleteEnvVar(viewProject.id, v.id)}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #fee2e2', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Action Output */}
            {actionOutput && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Output</h4>
                  <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => setActionOutput('')}>Clear</button>
                </div>
                <pre style={{
                  background: '#0f172a', color: '#e2e8f0', padding: 14, borderRadius: 8,
                  fontSize: 11, lineHeight: 1.5, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap'
                }}>{actionOutput}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
